import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@shared/components/ui/button';
import { formatDate } from '@shared/utils/FormatUtils';
import { Entry, EntryReceipt, getReceiptStatusLabel, RECEIPT_STATUS_COLORS } from '../../models/Entry';
import { FileText, Calendar, Clock, User, Building2, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';

interface ReceiptsInfoProps {
  entry: Entry;
}

export const ReceiptsInfo: React.FC<ReceiptsInfoProps> = ({ entry }) => {
  const { t } = useTranslation('merchandiseV3');
  const navigate = useNavigate();

  const receipts = entry.entry_receipts || [];
  const team = (entry as any).team;
  const user = (entry as any).user;

  if (receipts.length === 0 && !team && !user) {
    return null;
  }

  const handleNavigateToReceipt = (receipt: EntryReceipt) => {
    navigate(`/merchandise/${entry.id}/receipts/${receipt.id}`);
  };

  const canNavigateToReceipt = (status: EntryReceipt['status']) =>
    status === 'ABIERTA' || status === 'COMPLETADA' || status === 'CANCELADA';

  const getReceiptActionLabel = (status: EntryReceipt['status']) =>
    status === 'ABIERTA'
      ? t('edit.receipts.go_to_receipt', 'Ir a recepción')
      : t('edit.receipts.view_receipt', 'Ver recepción');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
      {/* Título y descripción */}
      <div className="lg:col-span-2">
        <h3 className="text-lg font-semibold text-foreground">{t('edit.receipts.title', 'Información adicional')}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t('edit.receipts.description', 'Datos del equipo y recepciones asociadas')}
        </p>
      </div>

      {/* Contenido */}
      <div className="lg:col-span-4 space-y-6">
        {/* Información del equipo y usuario */}
        {(team || user) && (
          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {t('edit.receipts.team_info', 'Información del equipo')}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {team && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('edit.receipts.team', 'Equipo')}</p>
                    <p className="text-sm font-medium text-foreground">{team.name}</p>
                  </div>
                </div>
              )}
              {user && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('edit.receipts.created_by', 'Creado por')}</p>
                    <p className="text-sm font-medium text-foreground">{user.name}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Lista de recepciones */}
        {receipts.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t('edit.receipts.receipts_title', 'Recepciones')}
              <span className="bg-muted px-2 py-0.5 rounded-full text-xs">{receipts.length}</span>
            </h4>

            <div className="space-y-3">
              {receipts.map((receipt) => (
                <div
                  key={receipt.id}
                  className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Información principal */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        <span className="font-mono text-sm font-medium text-foreground">{receipt.code}</span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${RECEIPT_STATUS_COLORS[receipt.status]}`}
                        >
                          {getReceiptStatusLabel(receipt.status, (key) =>
                            t(`receipt.status.${key.toLowerCase()}`, receipt.status),
                          )}
                        </span>
                        {receipt.requirements_result && (
                          <span
                            className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              receipt.requirements_result === 'INCUMPLE'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            }`}
                          >
                            {receipt.requirements_result === 'INCUMPLE' ? (
                              <AlertTriangle className="w-3 h-3" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3" />
                            )}
                            {receipt.requirements_result === 'INCUMPLE'
                              ? t('edit.receipts.non_compliance', 'Incumple')
                              : t('edit.receipts.compliant', 'Correcta')}
                          </span>
                        )}
                      </div>

                      {/* Fechas */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        {receipt.started_at && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {t('edit.receipts.started', 'Inicio')}: {formatDate(receipt.started_at)}
                          </span>
                        )}
                        {receipt.deadline_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {t('edit.receipts.deadline', 'Límite')}: {formatDate(receipt.deadline_at)}
                          </span>
                        )}
                      </div>

                      {/* Razón de cancelación si existe */}
                      {receipt.cancel_reason && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                          {t('edit.receipts.cancel_reason', 'Motivo')}: {receipt.cancel_reason}
                        </p>
                      )}
                    </div>

                    {/* Enlace a la pantalla de recepción (incl. finalizada / cancelada) */}
                    {canNavigateToReceipt(receipt.status) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleNavigateToReceipt(receipt)}
                        className="flex-shrink-0"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        {getReceiptActionLabel(receipt.status)}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
